// src/hooks/usePremiumStatus.js
/**
 * usePremiumStatus — cross-device premium check
 *
 * Source of truth: Supabase `passes` table, keyed by user_id.
 *
 * Flow:
 *   1. User pays via Razorpay
 *   2. /api/pwp-verify-payment inserts a row into `passes`:
 *        { user_id, plan_id, status: "active", start_at, expires_at, ... }
 *      and flips any previous "active" rows to "expired"
 *   3. This hook reads the latest active+future row for the current user
 *      from `passes` directly, so any device they log into with the same
 *      account sees isPremium = true immediately.
 *
 * Returns: { isPremium, premiumLoaded, refresh, pass }
 *   - isPremium: boolean
 *   - premiumLoaded: false while checking, true once resolved
 *   - refresh(): force a re-check (e.g. on return from payment success)
 *   - pass: the active row { plan_id, status, start_at, expires_at } or null
 *
 * In MOCK mode (REACT_APP_PWP_PAYWALL_MODE not set to "razorpay"/"live") this
 * falls back to localStorage `passStorage.hasActivePass()` for dev.
 *
 * Safety net: 5-second timeout so the UI never hangs on "Checking…" forever.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../auth/AuthProvider";
import { hasActivePass } from "../services/passStorage";

const MODE = (process.env.REACT_APP_PWP_PAYWALL_MODE || "mock").toLowerCase();
const IS_MOCK_MODE = !(MODE === "razorpay" || MODE === "live");

/**
 * Defensive check: is this `passes` row a currently-active pass?
 *
 * The Supabase query in the hook already filters by status='active' AND
 * expires_at > now(), so anything returned should pass this check. This is
 * belt-and-braces in case of clock skew or stale state from a cached read.
 *
 * Exported so ProtectedLevelRoute.jsx can use the exact same logic — that
 * guarantees the menu and the level guard never disagree about premium.
 */
export function isActivePassRow(row) {
  if (!row || typeof row !== "object") return false;
  if (row.status !== "active") return false;
  const expMs = row.expires_at ? new Date(row.expires_at).getTime() : NaN;
  return Number.isFinite(expMs) && expMs > Date.now();
}

export function usePremiumStatus() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [isPremium, setIsPremium] = useState(false);
  const [premiumLoaded, setPremiumLoaded] = useState(false);
  const [pass, setPass] = useState(null);

  // Bumping this triggers a re-fetch (used by `refresh()` below).
  const [refreshTick, setRefreshTick] = useState(0);

  // Used to ignore late responses if user logs out / changes mid-flight
  const aliveRef = useRef(true);

  // Track previous userId so we can tell a "user changed" run from a
  // "refresh() called" run. We only show "Checking…" on the former.
  const prevUserIdRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      // Not logged in → not premium
      if (!userId) {
        if (!cancelled && aliveRef.current) {
          setIsPremium(false);
          setPass(null);
          setPremiumLoaded(true);
        }
        return;
      }

      // 5-second timeout to prevent infinite "Checking..." state
      const timeoutId = setTimeout(() => {
        if (!cancelled && aliveRef.current) {
          console.warn("usePremiumStatus: pass check timeout (5s) — defaulting to free mode");
          setIsPremium(false);
          setPass(null);
          setPremiumLoaded(true);
        }
      }, 5000);

      try {
        // Mock mode: localStorage-backed (dev only)
        if (IS_MOCK_MODE) {
          clearTimeout(timeoutId);
          const active = hasActivePass();
          if (!cancelled && aliveRef.current) {
            setIsPremium(active);
            setPass(null); // mock mode doesn't track a server row
            setPremiumLoaded(true);
          }
          return;
        }

        // Real mode: read the latest active pass for this user from the
        // `passes` table. Same query shape as /api/pwp-entitlement on the
        // server, so the client and server can never disagree.
        const nowIso = new Date().toISOString();
        const { data, error } = await supabase
          .from("passes")
          .select("plan_id, status, start_at, expires_at")
          .eq("user_id", userId)
          .eq("status", "active")
          .gt("expires_at", nowIso)
          .order("expires_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        clearTimeout(timeoutId);

        if (cancelled || !aliveRef.current) return;

        if (error) {
          console.error("usePremiumStatus: passes query error:", error);
          setIsPremium(false);
          setPass(null);
        } else {
          const ok = isActivePassRow(data);
          if (!ok && data) {
            // Should never fire — query already filtered. Logs if it does.
            console.warn(
              "usePremiumStatus: passes row returned but isActivePassRow=false. Row:",
              data
            );
          }
          setIsPremium(ok);
          setPass(ok ? data : null);
        }
        setPremiumLoaded(true);
      } catch (err) {
        clearTimeout(timeoutId);
        console.error("usePremiumStatus: unexpected error:", err);
        if (!cancelled && aliveRef.current) {
          setIsPremium(false);
          setPass(null);
          setPremiumLoaded(true);
        }
      }
    }

    // Detect whether this run is because the user actually changed
    // (real loading state needed) or because refresh() was called
    // (silent background re-check — keep previous UI visible).
    const isUserChange = prevUserIdRef.current !== userId;
    prevUserIdRef.current = userId;
    if (isUserChange) {
      setPremiumLoaded(false);
    }
    check();

    return () => {
      cancelled = true;
    };
  }, [userId, refreshTick]);

  // Force a re-check (e.g. after returning from payment success)
  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  return { isPremium, premiumLoaded, refresh, pass };
}

export default usePremiumStatus;